/**
 * Error Handling Utilities
 *
 * Centralized error handling helpers for consistent error extraction
 * and processing across the widget.
 */

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard: Check if value is a non-null object
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Type guard: Check if value has a string message property
 */
export function hasMessage(value: unknown): value is { message: string } {
  return isObject(value) && typeof value.message === 'string';
}

/**
 * Type guard: Check if value has a string error property
 */
export function hasError(value: unknown): value is { error: string } {
  return isObject(value) && typeof value.error === 'string';
}

/**
 * Type guard: Check if value has a string code property
 */
export function hasCode(value: unknown): value is { code: string } {
  return isObject(value) && typeof value.code === 'string';
}

/**
 * Type guard: Check if value is an ErrorResponseData
 */
export function isErrorResponseData(value: unknown): value is ErrorResponseData {
  if (!isObject(value)) return false;
  // At least one of message, error, or code should be present
  return hasMessage(value) || hasError(value) || hasCode(value);
}

/**
 * Type guard: Check if value is an array
 */
export function isArray<T = unknown>(value: unknown): value is T[] {
  return Array.isArray(value);
}

/**
 * Type guard: Check if value is a string
 */
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Type guard: Check if value is a number
 */
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

/**
 * Type guard: Check if value is a boolean
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

// =============================================================================
// INTERFACES
// =============================================================================

/**
 * Standard error response structure from backend APIs.
 * Most APIs return errors in one of these formats.
 */
export interface ErrorResponseData {
  message?: string;
  error?: string;
  code?: string;
  details?: unknown;
}

/**
 * Extract error message and code from an API error response.
 *
 * Handles multiple error response formats:
 * - { message: "error text" }
 * - { error: "error text" }
 * - { message: "error text", code: "ERROR_CODE" }
 *
 * @param response The fetch Response object
 * @returns Object with errorMessage and optional errorCode
 */
export async function extractErrorFromResponse(
  response: Response
): Promise<{ errorMessage: string; errorCode?: string }> {
  const errorData = await response.json().catch(() => ({}));

  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  let errorCode: string | undefined;

  if (hasMessage(errorData)) {
    errorMessage = errorData.message;
  } else if (hasError(errorData)) {
    errorMessage = errorData.error;
  }

  if (hasCode(errorData)) {
    errorCode = errorData.code;
  }

  return { errorMessage, errorCode };
}

/**
 * Format a user-friendly error message from various error types.
 *
 * @param error The error to format
 * @param fallback Fallback message if error can't be formatted
 * @returns User-friendly error message
 */
export function formatErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred'
): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (isString(error)) {
    return error;
  }

  if (hasMessage(error)) {
    return error.message;
  }

  if (hasError(error)) {
    return error.error;
  }

  return fallback;
}

/**
 * Check if an error is an AbortError (from request cancellation/timeout).
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Check if an error is a network error (offline, DNS failure, etc.).
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  // Check for common network error patterns
  const message = error.message.toLowerCase();
  return (
    error.name === 'TypeError' && message.includes('fetch') ||
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('connection') ||
    message.includes('dns')
  );
}

/**
 * Create a standardized error message for logging.
 * Includes error type, message, and optional context.
 */
export function createErrorLog(
  error: unknown,
  context?: string
): string {
  const prefix = context ? `[${context}] ` : '';
  const errorName = error instanceof Error ? error.name : 'Error';
  const errorMessage = formatErrorMessage(error);

  return `${prefix}${errorName}: ${errorMessage}`;
}
